import {CommonDataService, Credential, CredentialYearRange, EnumType, Format, PrimeNgConfig, PrimeNgSupportService, RefDomainType, TableColumn, TableFormat, Validation} from '@app/shared';
import {AfterViewInit, Component, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {ConfirmationService, SelectItem} from 'primeng/api';
import {classToClass} from "class-transformer";

/**
 * @author Archana Anand (date: June 2018)
 * @desc CRUD operations for Educational Credentials of Employee
 * */

@Component({
  selector: 'app-educational-credential',
  templateUrl: './educational-credential.component.html',
  styleUrls: ['./educational-credential.component.css'],
  providers: [ConfirmationService]
})

export class EducationalCredentialComponent implements OnInit, AfterViewInit {

  @Input() credentials: Credential[];
  @Output() credentialChangeHandler = new EventEmitter<Credential[]>();

  // standard format and validation
  CredentialYearRange = CredentialYearRange;
  Format: typeof Format = Format;
  Validation: typeof Validation = Validation;
  PrimeNgConfig: typeof PrimeNgConfig = PrimeNgConfig;


  readonly DefaultRows = 3;
  readonly DefaultSortField = "type";

  tableColumns: TableColumn[];                                // column headers and fields to display on the table
  credentialToEdit: Credential;                         // object used when add/edit selected row
  credentialTypeDropDown: SelectItem[];
  credentialStatusTypeDropDown: SelectItem[];
  degreeMajorDropDown: SelectItem[];
  credentialGrantedDropDown: SelectItem[];
  displayDialog: boolean;                             // when true, show the popup dialog to gather user input
  selectedRowIndex: number;                           // index of the selected row
  maxDobEndOn: Date;

  @ViewChild('credentialAction') credentialActionTemplate: TemplateRef<any>;

  constructor(private commonDataService: CommonDataService, private confirmationService: ConfirmationService, private primeNgService: PrimeNgSupportService) {
  }

  ngOnInit() {
    this.maxDobEndOn = new Date();
    this.getCredentialTypes();
  }

  ngAfterViewInit(): void {
    Promise.resolve(null).then(() => this.buildMenu());
  }


  /**
   * @desc retrieve valid credential types
   * @returns this.credentialTypeDropDown - retrieved valid credential types as dropdown menu from the backend DB
   * */
  getCredentialTypes(): void {

    this.commonDataService.getEnumPairAsDropdown(EnumType.Credential).subscribe(credentialTypes => {
      this.credentialTypeDropDown = credentialTypes;
    });

    this.commonDataService.getEnumPairAsDropdown(EnumType.CredentialStatus).subscribe(credentialStatusTypes => {
      this.credentialStatusTypeDropDown = credentialStatusTypes;
    });

    this.commonDataService.getRefCodeAsDropdown(RefDomainType.DegreeMajor, true).subscribe(degreeMajorTypes => {
      this.degreeMajorDropDown = degreeMajorTypes;
    });

    this.commonDataService.getRefCodeAsDropdown(RefDomainType.Degree, true).subscribe(credentialGrantedTypes => {
      this.credentialGrantedDropDown = credentialGrantedTypes;
    });
  }

  /**
   * @desc show a pop-up dialog to gather new credential information
   * @returns this.copiedRow - instantiated
   *          this.displayDialog - set to true, so popup dialog is shown to gather a new credential info
   */
  onClickAddCredential(): void {
    this.credentialToEdit = new Credential();
    this.selectedRowIndex = -1;
    this.displayDialog = true;
  }

  /**
   * @desc edit an existing credential
   * @param credential - selected credential to edit
   * @returns this.copiedRow - has copy of the selected credential
   *          displayDialog - set it to true which in turn open up an edit dialog
   */
  onClickEditCredential(credential: Credential): void {
    const rowIndex = this.credentials.indexOf(credential);
    this.credentialToEdit = classToClass(credential);
    this.selectedRowIndex = rowIndex;
    this.displayDialog = true;
  }


  /**
   * @desc save new credential to the inputArray
   * @param credential - either new or selected credential for edit
   */
  onClickSaveCredential(credential: Credential): void {
    this.commonDataService.getRefCodeAsDropdown(RefDomainType.DegreeMajor).subscribe(degreeMajorTypes => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      credential.credentialId = degreeMajorTypes.find(degreeMajor => (degreeMajor.label == credential.degreeMajor)).value;

      if (this.selectedRowIndex >= 0) {
        this.credentials = this.primeNgService.replaceByIndex(this.credentials, credential, this.selectedRowIndex);
        this.selectedRowIndex = -1;
      } else {
        this.credentials = this.primeNgService.unshift(this.credentials, credential);
      }
      this.triggerValueChangedEvent();
    });
    this.displayDialog = false;
  }

  /**
   * @desc delete the selected row from the inputArray
   * @param credential - selected row to delete
   * @returns: this.inputArray - removed input row from the array
   */
  onClickDeleteCredential(credential: Credential): void {
    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this Credential?',
      header: 'Delete Credential',
      icon: 'fa fa-question-circle',
      accept: () => {
        this.credentials = this.primeNgService.deleteByObj(this.credentials, credential);
        this.triggerValueChangedEvent();
      },
      reject: () => {
      }
    });
  }

  triggerValueChangedEvent() {
    this.credentialChangeHandler.emit(this.credentials);
  }

  private buildMenu(): void {
    this.tableColumns = [
      {field: 'type', header: 'Type', format: TableFormat.TitleCase},
      {field: 'degreeMajor', header: 'Degree Major'},
      {field: 'grantingInstitution', header: 'Granting Institution'},
      {field: 'credentialGranted', header: 'Credential Granted'},
      {field: 'credentialGrantedOn', header: 'Granted On', format: TableFormat.Date},
      {field: 'status', header: 'Status', format: TableFormat.TitleCase},
      {field: 'action', header: 'Action', template: this.credentialActionTemplate},
    ];

  }
}
